import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { RefreshCw, Search, Upload } from 'lucide-react';
import type { AmazonCoupon } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { DATA_TABLE_WRAPPER } from '@/lib/data-table';
import {
  AMAZON_PAGE_SIZE,
  AMAZON_PAGE_SIZE_OPTIONS,
  LOW_POOL_THRESHOLD,
  MARKETPLACES,
  useAmazonCouponPools,
  useAmazonCoupons,
  useReconcileAmazonCoupons,
} from '../hooks/useAmazonCoupons';
import { AmazonCouponForm } from '../components/AmazonCouponForm';
import { AmazonCouponTable } from '../components/AmazonCouponTable';
import { AllocatedCouponDialog } from '../components/AllocatedCouponDialog';
import { AmazonCouponBatchDialog } from '../components/AmazonCouponBatchDialog';

const ALL = '__all__';
const STATUSES = ['ACTIVE', 'USED', 'EXPIRED'];
const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  USED: 'Used',
  // EXPIRED means the CAMPAIGN ended — the only expiry Amazon enforces.
  EXPIRED: 'Expired',
};

/**
 * Amazon Coupons — the third section of Devices Fulfillment (DevicesFulfillmentShell owns the h1).
 *
 * Same card-above-ledger order as DemoCouponsPage: the act comes first and the record follows.
 *
 * ONE THING ITS NEIGHBOURS DO NOT HAVE — a pool banner above both. The demo card mints, so it can
 * never be out of stock; this one allocates from a finite batch a human loaded, and "how many are
 * left" is a question that has to be answerable before a staffer promises anyone a code rather
 * than after.
 *
 * The page SCROLLS, like DemoCouponsPage and unlike Devices Order: a tall card stacked on a ledger
 * does not fit a laptop viewport, and pinning it clips the card with no way to reach what was cut.
 * The shell's outlet (`min-h-0 flex-1 overflow-auto`) does the scrolling.
 */
export function AmazonCouponsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number>(AMAZON_PAGE_SIZE);
  // Owned by the PAGE, not the form: a successful allocate refetches the ledger underneath, and a
  // dialog held by the thing that re-renders can vanish before it is read.
  const [allocated, setAllocated] = useState<AmazonCoupon | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importNote, setImportNote] = useState('');

  // ROOT gates the IMPORT only. Allocating is ROOT + FULFILLMENT, matching the resolver — and the
  // resolver refuses independently, so this is the UX half, never the control.
  const isRoot = !!useAuthStore((s) => s.adminUser)?.roles?.includes('root');

  // Page size resets the index with the filters: changing it while deep in the list would land on
  // a page that no longer exists and show an empty table over a non-zero count.
  useEffect(() => {
    setPageIndex(0);
  }, [search, status, marketplace, pageSize]);

  const { data: poolData, loading: poolsLoading } = useAmazonCouponPools();
  const pools = poolData?.amazonCouponPools ?? [];

  const { data, loading, error, refetch } = useAmazonCoupons(
    search,
    status,
    marketplace,
    pageIndex * pageSize,
    pageSize,
  );
  const page = data?.amazonCoupons;
  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const firstShown = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = Math.min(total, pageIndex * pageSize + items.length);

  const [reconcile, { loading: reconciling }] = useReconcileAmazonCoupons();

  const lowPools = pools.filter((p) => p.available > 0 && p.available < LOW_POOL_THRESHOLD);
  const emptyMarkets = MARKETPLACES.filter(
    (m) => !pools.some((p) => p.marketplace === m.value && p.available > 0),
  );

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="amazon-coupons-page">
      {/* No heading: the shell owns the page title and the active tab names this section. */}
      <p className="text-sm text-muted-foreground">
        {'Fixed-amount ($10 off) claim codes for customers buying OptiSigns hardware on Amazon. '}
        {'Every code is created in Seller Central and imported here — nothing in this app creates '}
        {'a promotion or a code, because Amazon publishes no API that can. This hands them out and '}
        {'records who got which. Sending the code to the customer is still your job. Redemptions '}
        {'come from Amazon once a day, per promotion — Amazon never reveals which code an order used.'}
      </p>

      {/* The pool banner. Above the card because it decides whether the card can be used at all. */}
      <div className="flex flex-wrap items-center gap-3" data-testid="amazon-pool-banner">
        {poolsLoading && pools.length === 0 ? (
          <span className="text-sm text-muted-foreground">{'Checking the pool…'}</span>
        ) : (
          MARKETPLACES.map((m) => {
            const pool = pools.find((p) => p.marketplace === m.value);
            const count = pool?.available ?? 0;
            const tone =
              count === 0
                ? 'border-destructive/50 text-destructive'
                : count < LOW_POOL_THRESHOLD
                  ? 'border-amber-500/50 text-amber-600'
                  : 'border-border text-foreground';
            return (
              <span
                key={m.value}
                className={`rounded-md border px-3 py-1.5 text-sm ${tone}`}
                data-testid={`amazon-pool-${m.value}`}
              >
                <strong>{m.label}</strong>
                {` — ${count} code${count === 1 ? '' : 's'} left`}
                {pool?.total ? ` of ${pool.total}` : ''}
              </span>
            );
          })
        )}

        {isRoot ? (
          <>
            <Button
              variant="outline"
              className="gap-1"
              onClick={() => setImportOpen(true)}
              data-testid="amazon-coupon-import"
            >
              <Upload className="h-4 w-4" />
              {'Import batch'}
            </Button>
            {/* The daily pass is the normal path; this is for when someone wants the number now. */}
            <Button
              variant="ghost"
              className="gap-1"
              disabled={reconciling}
              onClick={() => {
                void reconcile();
              }}
              data-testid="amazon-coupon-reconcile"
              title="Ask Amazon how many orders redeemed each promotion. Runs daily on its own."
            >
              <RefreshCw className={`h-4 w-4${reconciling ? ' animate-spin' : ''}`} />
              {reconciling ? 'Checking Amazon…' : 'Check redemptions'}
            </Button>
          </>
        ) : null}
      </div>

      {importNote ? (
        <p className="text-sm text-emerald-600" data-testid="amazon-import-note">
          {importNote}
        </p>
      ) : null}

      {(lowPools.length > 0 || emptyMarkets.length > 0) && !poolsLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="amazon-pool-warning">
          {emptyMarkets.length > 0
            ? `No codes left for ${emptyMarkets.map((m) => m.label).join(', ')}. `
            : ''}
          {lowPools.length > 0
            ? `Running low: ${lowPools.map((p) => `${p.marketplace.toUpperCase()} (${p.available})`).join(', ')}. `
            : ''}
          {isRoot
            ? 'Create a new Money Off promotion in Seller Central and import the CSV.'
            : 'Ask a root user to import a new batch from Seller Central.'}
        </p>
      ) : null}

      <AmazonCouponForm
        pools={pools}
        poolsLoading={poolsLoading}
        onAllocated={(c) => setAllocated(c)}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, customer, reason, who issued it, SKU…"
            className="pl-9"
            data-testid="amazon-search"
          />
        </div>

        <Select
          value={marketplace || ALL}
          onValueChange={(v) => setMarketplace(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[170px]" data-testid="amazon-filter-marketplace">
            <SelectValue placeholder="Marketplace" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All marketplaces'}</SelectItem>
            {MARKETPLACES.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? '' : v)}>
          <SelectTrigger className="w-[190px]" data-testid="amazon-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All statuses'}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s] ?? s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* ON THE FILTER ROW, pushed right (requester, 2026-09-24). They are the same control
            surface — what am I narrowing to, and how much of it am I seeing — so they share a
            line rather than stacking two rows of chrome above the table. */}
        <div className="ml-auto flex items-center gap-3 text-sm text-muted-foreground">
          <span data-testid="amazon-coupons-count">
            {loading && items.length === 0
              ? 'Loading…'
              : `${firstShown}-${lastShown} of ${total}`}
          </span>
          <span className="text-xs">{'Rows per page'}</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger
              className="h-8 w-[72px] cursor-pointer"
              aria-label={'Rows per page'}
              data-testid="amazon-page-size"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AMAZON_PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} className="cursor-pointer">
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {error ? (
          <div
            className="rounded-md border border-destructive p-4 text-sm text-destructive"
            data-testid="amazon-coupons-error"
          >
            {error.message}
          </div>
        ) : (
          <>
            {/* DATA_TABLE_WRAPPER — which OrderTaskTable and DemoCouponTable both omit, leaving
                their headers non-sticky (CHANGELOG 2026-08-26). New table, so it starts right. */}
            <div className={DATA_TABLE_WRAPPER}>
              <AmazonCouponTable coupons={items} />
            </div>

            {/* Paging stays BELOW the table, where you are when you run out of rows, and
                renders only when there is somewhere to go — a pair of permanently disabled
                buttons under a single-page list is furniture. */}
            {total > pageSize ? (
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pageIndex === 0}
                  onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                  data-testid="amazon-prev-page"
                >
                  {'Previous'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={lastShown >= total}
                  onClick={() => setPageIndex((i) => i + 1)}
                  data-testid="amazon-next-page"
                >
                  {'Next'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>

      <AllocatedCouponDialog coupon={allocated} onClose={() => setAllocated(null)} />

      {isRoot ? (
        <AmazonCouponBatchDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          onImported={(n) => {
            setImportNote(`Imported ${n} code${n === 1 ? '' : 's'}.`);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}
